import {GrantSource, GrantView, ImpersonationKind} from './grant-view';

/**
 * Chip kinds shown in the UI. `data-access-temporary` folds into 'data-access'
 * (it is the same access type, just time-boxed), while the two impersonation
 * kinds stay separate all the way through — they are different capabilities and
 * must never be summed or shown as one chip.
 */
export type ChipKind = 'data-access' | ImpersonationKind;

/**
 * Tab selector inside the Grants page. 'users' and 'tenants' are the two
 * primary aggregations (rows are users / rows are tenants); the per-kind tabs
 * are filtered views of the users aggregation. The separate Requests page has
 * its own state and isn't part of this enum.
 */
export type TabKind = ChipKind | 'users' | 'tenants';

/** Match a backend kind to a chip kind. Returns null for unknown kinds. */
export function tabKindOf(kind: GrantView['kind']): ChipKind | null {
    if (kind === 'imp-ai' || kind === 'imp-decisions') {
        return kind;
    }
    if (kind === 'data-access' || kind === 'data-access-temporary') {
        return 'data-access';
    }
    return null;
}

/** The per-application impersonation tabs, in the order they render everywhere. */
export const IMPERSONATION_TABS: readonly ImpersonationKind[] = ['imp-ai', 'imp-decisions'];

export interface TenantChip {
    /** First raw id seen for this tenant (could be a shard uid or an AWS account id). */
    resource: string;
    /** Human-readable name for the tenant; falls through to resource when unknown. */
    resourceName?: string;
    /** All raw ids that collapsed into this chip (deduplicated, in insertion order). */
    resourceIds: string[];
    kind: ChipKind;
    permanent: boolean;
    daysRemaining: number;
    /** Whole hours remaining until the earliest non-permanent expiry. Negative = past. */
    hoursRemaining: number;
    fromBastion: boolean;
    grants: GrantView[];
}

export interface UserRow {
    email: string;
    name: string;
    chips: TenantChip[];
}

/** Whole number of days from now until `iso`. Negative = past. */
export function daysUntil(iso: string | undefined, now = Date.now()): number {
    if (!iso) {
        return Number.POSITIVE_INFINITY;
    }
    const t = new Date(iso).getTime();
    if (!Number.isFinite(t)) {
        return Number.POSITIVE_INFINITY;
    }
    return Math.floor((t - now) / 86_400_000);
}

/** Whole hours remaining until `iso`. Negative = past. */
export function hoursUntil(iso: string | undefined, now = Date.now()): number {
    if (!iso) {
        return Number.POSITIVE_INFINITY;
    }
    const t = new Date(iso).getTime();
    if (!Number.isFinite(t)) {
        return Number.POSITIVE_INFINITY;
    }
    return Math.floor((t - now) / 3_600_000);
}

/** Pretty-print days remaining for a chip badge. */
export function expiryLabel(days: number | null): string {
    if (days === null || days === Number.POSITIVE_INFINITY) {
        return '∞';
    }
    if (days < 0) {
        return 'exp';
    }
    if (days < 100) {
        return `${days}d`;
    }
    return `${Math.round(days / 30)}mo`;
}

/** Derive a display name from an email (local part, title-cased). */
export function nameFromEmail(email: string): string {
    const local = email.split('@')[0] ?? email;
    return local
        .split(/[._-]+/)
        .filter(Boolean)
        .map(p => p.charAt(0).toUpperCase() + p.slice(1))
        .join(' ');
}

/** Initials for an avatar — first letter of name, first letter of last token. */
export function initials(name: string, fallback: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) {
        return fallback.slice(0, 2).toUpperCase();
    }
    if (parts.length === 1) {
        return parts[0].slice(0, 2).toUpperCase();
    }
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Group raw grants of a given kind by user/email, then by tenant.
 *
 * Per the design: a tenant chip aggregates every grant (user × tenant × kind),
 * and surfaces three derived signals — permanent (any underlying grant has no
 * expiry), daysRemaining (min over non-permanent), fromBastion (any source is
 * iam-bastion).
 */
/** Canonical id for a grant's resource: the name when known, raw id otherwise. */
export function canonicalResource(g: Pick<GrantView, 'resource' | 'resourceName'>): string {
    return g.resourceName ?? g.resource;
}

export function aggregateUserAccess(grants: GrantView[], tab: TabKind, now = Date.now()): UserRow[] {
    // On the 'users' tab a user can hold both data-access and impersonation against
    // the same tenant; they're semantically different chips, so we bucket by
    // (resource, kind) instead of just resource. Per-kind tabs ignore the other.
    // 'tenants' tab uses a separate aggregateByTenant function.
    //
    // The bucket key uses the *canonical* resource — i.e. the tenant name when
    // we know it — so grants that arrived as a shard uid and as a 12-digit
    // AWS account id (IAM) for the same tenant merge into one chip instead of two.
    const byUser = new Map<string, Map<string, GrantView[]>>();
    const bucketKey = (g: GrantView, kind: ChipKind) => `${kind}|${canonicalResource(g)}`;

    for (const g of grants) {
        const chipKind = tabKindOf(g.kind);
        if (!chipKind) {
            continue;
        }
        if (tab !== 'users' && tab !== 'tenants' && chipKind !== tab) {
            continue;
        }
        let perUser = byUser.get(g.email);
        if (!perUser) {
            perUser = new Map();
            byUser.set(g.email, perUser);
        }
        const key = bucketKey(g, chipKind);
        const bucket = perUser.get(key);
        if (bucket) {
            bucket.push(g);
        } else {
            perUser.set(key, [g]);
        }
    }

    const rows: UserRow[] = [];
    for (const [email, byTenant] of byUser) {
        const chips: TenantChip[] = [];
        for (const [, bucket] of byTenant) {
            const first = bucket[0];
            const chipKind = tabKindOf(first.kind) as ChipKind;
            const permanent = bucket.some(g => !g.expiresAt);
            const fromBastion = bucket.some(g => g.source === 'iam-bastion');
            const expiring = bucket.filter(g => !!g.expiresAt);
            const minDays = expiring
                .map(g => daysUntil(g.expiresAt, now))
                .reduce((acc, d) => Math.min(acc, d), Number.POSITIVE_INFINITY);
            const minHours = expiring
                .map(g => hoursUntil(g.expiresAt, now))
                .reduce((acc, d) => Math.min(acc, d), Number.POSITIVE_INFINITY);
            const resourceIds: string[] = [];
            for (const g of bucket) {
                if (!resourceIds.includes(g.resource)) {
                    resourceIds.push(g.resource);
                }
            }
            chips.push({
                resource: first.resource,
                resourceName: bucket.find(g => g.resourceName)?.resourceName,
                resourceIds,
                kind: chipKind,
                permanent,
                daysRemaining: permanent ? Number.POSITIVE_INFINITY : minDays,
                hoursRemaining: permanent ? Number.POSITIVE_INFINITY : minHours,
                fromBastion,
                grants: bucket,
            });
        }
        chips.sort(compareChips);
        rows.push({email, name: nameFromEmail(email) || email, chips});
    }
    rows.sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email));
    return rows;
}

/**
 * Per-tenant view: rows are tenants, each row has two lists of users (one per
 * chip kind). Used by the Tenants tab. Sorted by user count desc (most populated
 * first) with the wildcard always at the top.
 */
export interface UserAccessSummary {
    email: string;
    name: string;
    kind: ChipKind;
    permanent: boolean;
    daysRemaining: number;
    hoursRemaining: number;
    fromBastion: boolean;
    grants: GrantView[];
}

export interface TenantRow {
    /** First raw id seen for this tenant. */
    resource: string;
    resourceName?: string;
    /** All raw ids (shard uid + account id, etc.) that map to this tenant. */
    resourceIds: string[];
    /**
     * Users per capability. Keyed by ChipKind rather than one field each, so
     * adding an application is a map entry instead of another field, another
     * sort and another render branch.
     */
    usersByKind: Record<ChipKind, UserAccessSummary[]>;
    totalUsers: number;
}

/** An empty per-kind bucket set. */
export function emptyUsersByKind(): Record<ChipKind, UserAccessSummary[]> {
    return {'data-access': [], 'imp-ai': [], 'imp-decisions': []};
}

export function aggregateByTenant(grants: GrantView[], now = Date.now()): TenantRow[] {
    interface Acc {
        resource: string;
        resourceName?: string;
        resourceIds: string[];
        byUserKind: Map<string, GrantView[]>;
    }
    const byTenant = new Map<string, Acc>();
    for (const g of grants) {
        const chipKind = tabKindOf(g.kind);
        if (!chipKind) {
            continue;
        }
        const canon = canonicalResource(g);
        let acc = byTenant.get(canon);
        if (!acc) {
            acc = {
                resource: g.resource,
                resourceName: g.resourceName,
                resourceIds: [g.resource],
                byUserKind: new Map(),
            };
            byTenant.set(canon, acc);
        } else {
            if (!acc.resourceName && g.resourceName) {
                acc.resourceName = g.resourceName;
            }
            if (!acc.resourceIds.includes(g.resource)) {
                acc.resourceIds.push(g.resource);
            }
        }
        const key = `${chipKind}|${g.email}`;
        const bucket = acc.byUserKind.get(key);
        if (bucket) {
            bucket.push(g);
        } else {
            acc.byUserKind.set(key, [g]);
        }
    }

    const rows: TenantRow[] = [];
    for (const acc of byTenant.values()) {
        const usersByKind = emptyUsersByKind();
        const seenEmails = new Set<string>();
        for (const [key, bucket] of acc.byUserKind) {
            const [chipKindStr, email] = key.split('|');
            const chipKind = chipKindStr as ChipKind;
            seenEmails.add(email);
            const permanent = bucket.some(g => !g.expiresAt);
            const fromBastion = bucket.some(g => g.source === 'iam-bastion');
            const expiring = bucket.filter(g => !!g.expiresAt);
            const minDays = expiring
                .map(g => daysUntil(g.expiresAt, now))
                .reduce((a, d) => Math.min(a, d), Number.POSITIVE_INFINITY);
            const minHours = expiring
                .map(g => hoursUntil(g.expiresAt, now))
                .reduce((a, d) => Math.min(a, d), Number.POSITIVE_INFINITY);
            const summary: UserAccessSummary = {
                email,
                name: nameFromEmail(email) || email,
                kind: chipKind,
                permanent,
                daysRemaining: permanent ? Number.POSITIVE_INFINITY : minDays,
                hoursRemaining: permanent ? Number.POSITIVE_INFINITY : minHours,
                fromBastion,
                grants: bucket,
            };
            usersByKind[chipKind].push(summary);
        }
        const byName = (a: UserAccessSummary, b: UserAccessSummary) => a.name.localeCompare(b.name);
        for (const bucket of Object.values(usersByKind)) {
            bucket.sort(byName);
        }
        rows.push({
            resource: acc.resource,
            resourceName: acc.resourceName,
            resourceIds: acc.resourceIds,
            usersByKind,
            totalUsers: seenEmails.size,
        });
    }
    rows.sort((a, b) => {
        // Wildcard always at the top — it grants access to everything, so an
        // admin should see who holds it first.
        if (a.resource === '*') {
            return -1;
        }
        if (b.resource === '*') {
            return 1;
        }
        if (a.totalUsers !== b.totalUsers) {
            return b.totalUsers - a.totalUsers;
        }
        return (a.resourceName ?? a.resource).localeCompare(b.resourceName ?? b.resource);
    });
    return rows;
}

/** Wildcard first → expiring soonest → permanent alpha. */
export function compareChips(a: TenantChip, b: TenantChip): number {
    const aw = a.resource === '*';
    const bw = b.resource === '*';
    if (aw !== bw) {
        return aw ? -1 : 1;
    }
    if (a.permanent !== b.permanent) {
        return a.permanent ? 1 : -1;
    }
    if (!a.permanent && !b.permanent && a.daysRemaining !== b.daysRemaining) {
        return a.daysRemaining - b.daysRemaining;
    }
    return a.resource.localeCompare(b.resource);
}

/** Total users across every capability in a tenant row. */
export function tenantRowUserCount(row: TenantRow, kinds: readonly ChipKind[]): number {
    return kinds.reduce((n, k) => n + row.usersByKind[k].length, 0);
}

/** Map a backend source to a friendly label. */
export function sourceLabel(source: GrantSource): string {
    switch (source) {
        case 'iam-bastion':
            return 'IAM · Bastion';
        case 'access-ops':
            return 'AccessOps';
    }
}

/* ----------------------------- Smoke tests ----------------------------- */
/* Cheap dev-only sanity checks. No jest in this repo yet; these run at import.
 * They are deliberately small and avoid network/date flakiness. */
if (import.meta.env.DEV) {
    const t0 = Date.UTC(2026, 0, 1);
    const iso = (offsetDays: number) => new Date(t0 + offsetDays * 86_400_000).toISOString();

    const sample: GrantView[] = [
        {id: '1', source: 'iam-bastion', email: 'a@x', resource: 'acme', kind: 'data-access'},
        {id: '2', source: 'iam-bastion', email: 'a@x', resource: 'acme', kind: 'data-access', expiresAt: iso(5)},
        {id: '3', source: 'iam-bastion', email: 'a@x', resource: '*', kind: 'data-access'},
        {id: '4', source: 'access-ops', email: 'a@x', resource: 'acme', kind: 'imp-decisions', expiresAt: iso(20)},
        {id: '5', source: 'iam-bastion', email: 'b@x', resource: 'acme', kind: 'data-access-temporary', expiresAt: iso(-3)},
        // 12 hours from t0
        {id: '6', source: 'access-ops', email: 'c@x', resource: 'beta', kind: 'imp-decisions', expiresAt: new Date(t0 + 12 * 3_600_000).toISOString()},
        // Same person, same tenant, the OTHER application — must stay a separate chip.
        {id: '7', source: 'access-ops', email: 'a@x', resource: 'acme', kind: 'imp-ai'},
        // Two wildcards, one per application: two rows, never merged.
        {id: '8', source: 'access-ops', email: 'a@x', resource: '*', kind: 'imp-ai'},
    ];

    const data = aggregateUserAccess(sample, 'data-access', t0);
    console.assert(data.length === 2, 'expect 2 users on data-access tab');
    const aRow = data.find(r => r.email === 'a@x');
    console.assert(aRow?.chips[0]?.resource === '*', 'wildcard must come first');
    const acme = aRow?.chips.find(c => c.resource === 'acme');
    console.assert(acme?.permanent === true, 'acme is permanent (grant #1 has no expiry)');
    console.assert(acme?.fromBastion === true, 'acme has bastion source');

    const bRow = data.find(r => r.email === 'b@x');
    console.assert(bRow?.chips[0]?.daysRemaining === -3, 'data-access-temporary flows into data-access tab');

    const impDec = aggregateUserAccess(sample, 'imp-decisions', t0);
    console.assert(impDec.length === 2, 'expect 2 users on the Decisions tab');
    console.assert(
        impDec.find(r => r.email === 'a@x')?.chips[0]?.daysRemaining === 20,
        'Decisions chip days'
    );

    const impAi = aggregateUserAccess(sample, 'imp-ai', t0);
    console.assert(impAi.length === 1, 'expect 1 user on the Ennabl AI tab');
    const aiChips = impAi[0]?.chips ?? [];
    console.assert(aiChips.length === 2, 'Ennabl AI: wildcard + acme');
    console.assert(aiChips[0]?.resource === '*', 'wildcard first on the AI tab too');
    // The isolation that matters: neither tab can see the other application.
    console.assert(
        impDec.every(r => r.chips.every(c => c.kind === 'imp-decisions')),
        'Decisions tab must contain ONLY imp-decisions chips'
    );
    console.assert(
        impAi.every(r => r.chips.every(c => c.kind === 'imp-ai')),
        'Ennabl AI tab must contain ONLY imp-ai chips'
    );
    // ...and on the combined Users tab the same tenant yields one chip per kind.
    const usersTab = aggregateUserAccess(sample, 'users', t0).find(r => r.email === 'a@x');
    const acmeChips = usersTab?.chips.filter(c => c.resource === 'acme') ?? [];
    console.assert(acmeChips.length === 3, 'acme: data-access + imp-ai + imp-decisions');

    console.assert(expiryLabel(null) === '∞');
    console.assert(expiryLabel(-1) === 'exp');
    console.assert(expiryLabel(7) === '7d');
    console.assert(expiryLabel(120) === '4mo');

    const imminent = aggregateUserAccess(sample, 'imp-decisions', t0).find(r => r.email === 'c@x')?.chips[0];
    console.assert(imminent?.daysRemaining === 0, 'imminent chip is < 1 day');
    console.assert(imminent?.hoursRemaining === 12, 'imminent chip has 12h remaining');
}
